// =====================================================================
// S-03 · Cobertura de garantía — cálculo PURO y compartido (api + web).
// Sin dependencias: el panel y el servidor tienen que decir exactamente lo
// mismo sobre si una garantía entra o no entra en cobertura.
// =====================================================================

/** Un modelo del catálogo con su período de garantía, en meses. */
export interface ModeloConCobertura {
  nombre: string;
  mesesCobertura: number;
}

export interface Cobertura {
  /** Días desde la instalación. null = no hay fecha de instalación. */
  diasTranscurridos: number | null;
  /** Meses de cobertura del modelo. null = el modelo no está en el catálogo. */
  mesesCobertura: number | null;
  /** true dentro, false fuera, null = falta un dato para decidirlo. */
  dentroDeCobertura: boolean | null;
  /** Fecha en la que vence (o venció) la cobertura. null = indeterminable. */
  venceEl: Date | null;
}

const MS_POR_DIA = 24 * 60 * 60 * 1000;

/**
 * Suma meses de CALENDARIO. 12 meses desde el 31 de enero vencen el 31 de enero
 * siguiente, no a los 360 días: el cliente cuenta en meses, no en bloques de 30.
 * Si el mes destino no tiene ese día (31 de marzo + 1 mes), se usa el último día
 * de ese mes en vez de derramar al siguiente.
 */
function sumarMeses(fecha: Date, meses: number): Date {
  const d = new Date(fecha.getTime());
  const diaOriginal = d.getUTCDate();
  d.setUTCMonth(d.getUTCMonth() + meses);
  if (d.getUTCDate() < diaOriginal) d.setUTCDate(0);
  return d;
}

/**
 * Días transcurridos desde la instalación y si el equipo sigue dentro del
 * período configurado para su modelo.
 *
 * Cuando falta un dato NO se inventa una respuesta: `null` significa "no se
 * puede saber" y la pantalla lo muestra como tal. Decirle "fuera de cobertura"
 * a un cliente porque nos falta un dato sería peor que no decir nada.
 */
export function coberturaDeGarantia(input: {
  modelo: string | null | undefined;
  fechaInstalacion: Date | null | undefined;
  modelos: ModeloConCobertura[];
  ahora?: Date;
}): Cobertura {
  const ahora = input.ahora ?? new Date();
  const fecha = input.fechaInstalacion ?? null;

  const diasTranscurridos =
    fecha == null
      ? null
      : // Una fecha futura (error de carga) da 0, no un negativo: "hace -12 días"
        // no significa nada para quien lee la ficha.
        Math.max(0, Math.floor((ahora.getTime() - fecha.getTime()) / MS_POR_DIA));

  const modeloCatalogo = input.modelo
    ? input.modelos.find((m) => m.nombre === input.modelo)
    : undefined;
  const mesesCobertura = modeloCatalogo?.mesesCobertura ?? null;

  if (fecha == null || mesesCobertura == null) {
    return { diasTranscurridos, mesesCobertura, dentroDeCobertura: null, venceEl: null };
  }

  const venceEl = sumarMeses(fecha, mesesCobertura);
  return {
    diasTranscurridos,
    mesesCobertura,
    dentroDeCobertura: ahora.getTime() <= venceEl.getTime(),
    venceEl,
  };
}
